import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { BrandConfig } from 'shared';
import { api } from './api.js';

/**
 * Developer-set branding (see apps/api/src/env.ts), read once at startup
 * from the unauthenticated `GET /api/brand`. Unlike `theme.tsx`, there is no
 * setter at all — not for a role, not for the admin, not for anyone —
 * because neither half of this is a database row any policy could reach.
 *
 * Two independent values, not one: `product` is the software's own name
 * (replaces "Perla"/"PerlaProp" mentions); `company` is the management
 * agency running this instance (the header's brand slot only).
 *
 * Falls back to today's literal defaults on a failed fetch, so a network
 * hiccup degrades to current behavior rather than blanking the header.
 */
const DEFAULT_BRAND: BrandConfig = {
  product: { name: 'PerlaProp' },
  company: { name: 'PerlaProp' },
};

const BrandContext = createContext<BrandConfig>(DEFAULT_BRAND);

export function BrandProvider({ children }: { children: ReactNode }) {
  const [brand, setBrand] = useState<BrandConfig>(DEFAULT_BRAND);

  useEffect(() => {
    let cancelled = false;
    void api<BrandConfig>('/brand')
      .then((config) => {
        if (!cancelled) setBrand(config);
      })
      .catch(() => {
        // Keep DEFAULT_BRAND — see the doc comment above.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // The browser tab identifies "whose portal is this," same question the
  // header answers — so it tracks the company brand, not the product name.
  useEffect(() => {
    document.title = brand.company.name;
  }, [brand.company.name]);

  return <BrandContext.Provider value={brand}>{children}</BrandContext.Provider>;
}

export function useBrand(): BrandConfig {
  return useContext(BrandContext);
}
